/**
 * K Matrix Content 컴포넌트
 *
 * K 행렬 데이터를 모달 등에서 표시하기 위한 컴포넌트
 * AITrackerContent의 K 보기 탭 기능을 독립 컴포넌트로 분리
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchMathConcepts } from '../utils/mathConceptsUtils';
import { AlertCircle, Layers, RefreshCw, BarChart3, Table, ZoomIn, ZoomOut, Maximize2, ChevronDown, Check } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// Incoming 엣지 소스별 색상 팔레트
const INCOMING_COLORS = [
  { main: '#3b82f6', light: '#dbeafe', name: '파랑', tailwind: 'bg-blue-500' },
  { main: '#8b5cf6', light: '#ede9fe', name: '보라', tailwind: 'bg-violet-500' },
  { main: '#ec4899', light: '#fce7f3', name: '분홍', tailwind: 'bg-pink-500' },
  { main: '#f97316', light: '#ffedd5', name: '주황', tailwind: 'bg-orange-500' },
  { main: '#14b8a6', light: '#ccfbf1', name: '청록', tailwind: 'bg-teal-500' },
  { main: '#eab308', light: '#fef9c3', name: '노랑', tailwind: 'bg-yellow-500' },
  { main: '#ef4444', light: '#fee2e2', name: '빨강', tailwind: 'bg-red-500' },
  { main: '#6366f1', light: '#e0e7ff', name: '인디고', tailwind: 'bg-indigo-500' },
];

// concept_type에 따른 색상 매핑
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',
    'AXM': 'rgba(249, 115, 22, 0.5)',
    'IDN': 'rgba(245, 158, 11, 0.5)',
    'THM': 'rgba(34, 197, 94, 0.5)',
    'MTH': 'rgba(6, 182, 212, 0.5)',
    'PRP': 'rgba(59, 130, 246, 0.5)',
    'FOR': 'rgba(139, 92, 246, 0.5)',
    'NTN': 'rgba(236, 72, 153, 0.5)'
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)';
};

// math_type에 따른 색상 매핑
const getMathTypeColor = (type) => {
  const colors = {
    'AL': 'rgba(99, 102, 241, 0.5)',
    'AN': 'rgba(244, 63, 94, 0.5)',
    'GE': 'rgba(16, 185, 129, 0.5)',
    'PS': 'rgba(168, 85, 247, 0.5)',
  };
  return colors[type] || 'rgba(107, 114, 128, 0.5)';
};

// Math Type 목록
const MATH_TYPE_LIST = [
  { code: 'AL', name: '대수학' },
  { code: 'AN', name: '해석학' },
  { code: 'GE', name: '기하학' },
  { code: 'PS', name: '확률통계' },
];

// Concept Type 목록
const CONCEPT_TYPES = [
  { code: 'DEF', name: '정의' },
  { code: 'AXM', name: '공리' },
  { code: 'IDN', name: '정리' },
  { code: 'THM', name: '이론' },
  { code: 'MTH', name: '방법론' },
  { code: 'PRP', name: '성질' },
  { code: 'FOR', name: '공식' },
  { code: 'NTN', name: '표기법' },
];

function KMatrixContent() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // Math Concepts 상태
  const [mathConcepts, setMathConcepts] = useState([]);

  // K 탭 상태
  const [kMeta, setKMeta] = useState(null);
  const [kRows, setKRows] = useState(null);
  const [kVectorNi, setKVectorNi] = useState(null);
  const [kVectorSRow, setKVectorSRow] = useState(null);
  const [isLoadingK, setIsLoadingK] = useState(false);
  const [kError, setKError] = useState(null);
  const [expandedKRows, setExpandedKRows] = useState(() => new Set());

  // 테이블 정렬 상태: { column: string | null, direction: 'asc' | 'desc' | null }
  const [tableSort, setTableSort] = useState({ column: null, direction: null });

  // 탭 상태
  const [activeTab, setActiveTab] = useState('graph'); // 'graph' | 'table'

  // 그래프 줌/팬 상태
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);  // 실제 드래그가 발생했는지 추적
  const [lastTouchDistance, setLastTouchDistance] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null); // 선택된 노드
  const [weightRange, setWeightRange] = useState([0.3, 1]); // 엣지 가중치 범위 필터 [min, max]
  const [edgePopup, setEdgePopup] = useState(null); // 엣지 팝업 { type: 'outgoing' | 'incoming', position: {x, y} }

  // Type 필터 상태
  const [selectedTypes, setSelectedTypes] = useState([]); // 선택된 Concept Type
  const [selectedMathTypes, setSelectedMathTypes] = useState([]); // 선택된 Math Type
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [typeFilterPosition, setTypeFilterPosition] = useState({ top: 0, left: 0 });
  const outgoingBtnRef = useRef(null);
  const incomingBtnRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  const kLoadedRef = useRef(false);

  // LaTeX 렌더링 함수
  const renderLatex = useCallback((text) => {
    if (!text) return '';

    try {
      const latexPattern = /\$([^$]+)\$/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = latexPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }

        const latex = match[1];
        const html = katex.renderToString(latex, {
          throwOnError: false,
          displayMode: false
        });
        parts.push(`<span class="katex-inline">${html}</span>`);

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.join('');
    } catch (error) {
      console.error('LaTeX rendering error:', error);
      return text;
    }
  }, []);

  // Type 필터 토글 함수들
  const toggleConceptTypeFilter = useCallback((typeCode) => {
    setSelectedTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
  }, []);

  const toggleMathTypeFilter = useCallback((typeCode) => {
    setSelectedMathTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
  }, []);

  const toggleAllConceptTypes = useCallback(() => {
    if (selectedTypes.length === CONCEPT_TYPES.length) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(CONCEPT_TYPES.map(t => t.code));
    }
  }, [selectedTypes.length]);

  const toggleAllMathTypes = useCallback(() => {
    if (selectedMathTypes.length === MATH_TYPE_LIST.length) {
      setSelectedMathTypes([]);
    } else {
      setSelectedMathTypes(MATH_TYPE_LIST.map(t => t.code));
    }
  }, [selectedMathTypes.length]);

  const resetAllFilters = useCallback(() => {
    setSelectedTypes([]);
    setSelectedMathTypes([]);
  }, []);

  const totalSelectedFilters = selectedTypes.length + selectedMathTypes.length;

  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      try {
        const concepts = await fetchMathConcepts();
        setMathConcepts(concepts);
      } catch (err) {
        console.error('Math concepts 로드 실패:', err);
      }
    };
    loadMathConcepts();
  }, []);

  // mathConcepts를 Map으로 변환 (O(1) 조회용)
  const mathConceptsMap = useMemo(() => {
    const map = new Map();
    mathConcepts.forEach(c => map.set(String(c.id), c));
    return map;
  }, [mathConcepts]);

  const getConceptDisplayName = useCallback((cid) => {
    const concept = mathConceptsMap.get(String(cid));
    const raw = concept?.text || `개념 ${cid}`;
    const display = raw.includes(':') ? raw.split(':').slice(1).join(':').trim() : raw;
    return { concept, rendered: renderLatex(display) };
  }, [mathConceptsMap, renderLatex]);

  const decodeQ = (q, scale) => {
    if (q === null || q === undefined) return null;
    if (!scale) return q;
    return q / scale;
  };

  const fetchKAll = useCallback(async (isForcedReload = false) => {
    try {
      setIsLoadingK(true);

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const reloadParam = isForcedReload ? '1' : '0';

      const metaRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/k/meta?include_concept_index=1&force_reload=${reloadParam}`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (!metaRes.ok) {
        if (metaRes.status === 403) throw new Error('접근 권한이 없습니다. Manager 권한이 필요합니다.');
        throw new Error('K meta를 불러오는데 실패했습니다.');
      }
      const metaJson = await metaRes.json();

      const vecNiRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/k/vector?kind=N_i&force_reload=${reloadParam}`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (!vecNiRes.ok) throw new Error('K vector(N_i)를 불러오는데 실패했습니다.');
      const vecNiJson = await vecNiRes.json();

      const vecSRowRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/k/vector?kind=s_row&force_reload=${reloadParam}`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (!vecSRowRes.ok) throw new Error('K vector(s_row)를 불러오는데 실패했습니다.');
      const vecSRowJson = await vecSRowRes.json();

      const rowsRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/k/rows?force_reload=${reloadParam}`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (!rowsRes.ok) throw new Error('K rows를 불러오는데 실패했습니다.');
      const rowsJson = await rowsRes.json();

      setKMeta(metaJson.data);
      setKVectorNi(vecNiJson.data);
      setKVectorSRow(vecSRowJson.data);
      setKRows(rowsJson.data);
      setKError(null);
      kLoadedRef.current = true;
      return {
        meta: metaJson.data,
        vectorNi: vecNiJson.data,
        vectorSRow: vecSRowJson.data,
        rows: rowsJson.data,
      };
    } catch (err) {
      console.error('K 로드 오류:', err);
      setKError(err.message);
      return null;
    } finally {
      setIsLoadingK(false);
    }
  }, [getAccessToken, navigate]);

  // 초기 로드
  useEffect(() => {
    if (!kLoadedRef.current) {
      fetchKAll(false);
    }
  }, [fetchKAll]);

  // 강제 리로드 핸들러
  const handleForceReload = useCallback(() => {
    fetchKAll(true);
  }, [fetchKAll]);

  const kTableRows = useMemo(() => {
    const conceptIndex = kMeta?.meta?.concept_index;
    if (!conceptIndex || !kRows || !kVectorNi || !kVectorSRow) return [];

    const wScale = kRows.w_scale || 10000;

    // 모든 concept ID를 숫자로 통일 (타입 불일치 방지)
    const rowsBySrc = new Map();
    (kRows.rows || []).forEach((row) => {
      rowsBySrc.set(Number(row.src), row);
    });

    const niByCid = new Map();
    (kVectorNi.cids || []).forEach((cid, i) => {
      niByCid.set(Number(cid), kVectorNi.values?.[i]);
    });

    const sRowByCid = new Map();
    (kVectorSRow.cids || []).forEach((cid, i) => {
      sRowByCid.set(Number(cid), kVectorSRow.values?.[i]);
    });

    return conceptIndex.map((rawCid) => {
      const cid = Number(rawCid);
      const name = getConceptDisplayName(cid);
      const row = rowsBySrc.get(cid) || { dst: [], w: [] };
      const dst = row.dst || [];
      const w = row.w || [];

      // dst도 숫자로 통일
      const pairs = dst.map((d, i) => ({ dst: Number(d), wq: w[i] ?? 0 }));
      pairs.sort((a, b) => (b.wq || 0) - (a.wq || 0));
      const top = pairs.slice(0, 5).map(p => ({ dst: p.dst, w: decodeQ(p.wq, wScale) }));

      return {
        cid,
        nameRendered: name.rendered,
        n_i: niByCid.get(cid) ?? 0,
        s_row: sRowByCid.get(cid) ?? 0,
        outDegree: dst.length,
        edges: pairs.map(p => ({ dst: p.dst, w: decodeQ(p.wq, wScale) })),
        topEdges: top,
      };
    });
  }, [kMeta, kRows, kVectorNi, kVectorSRow, getConceptDisplayName]);

  // 정렬된 테이블 rows
  const sortedTableRows = useMemo(() => {
    if (!kTableRows.length) return [];
    if (!tableSort.column || !tableSort.direction) return kTableRows;

    const sorted = [...kTableRows];
    const { column, direction } = tableSort;
    const multiplier = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (column) {
        case 'cid':
          aVal = a.cid;
          bVal = b.cid;
          break;
        case 'n_i':
          aVal = a.n_i;
          bVal = b.n_i;
          break;
        case 's_row':
          aVal = a.s_row;
          bVal = b.s_row;
          break;
        case 'outDegree':
          aVal = a.outDegree;
          bVal = b.outDegree;
          break;
        case 'topEdges':
          aVal = a.topEdges.length;
          bVal = b.topEdges.length;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return -1 * multiplier;
      if (aVal > bVal) return 1 * multiplier;
      return 0;
    });

    return sorted;
  }, [kTableRows, tableSort]);

  // 테이블 헤더 클릭 핸들러 (내림차순 → 오름차순 → 기본 순환)
  const handleTableSort = useCallback((column) => {
    setTableSort((prev) => {
      if (prev.column !== column) {
        // 새 컬럼 선택: 내림차순부터 시작
        return { column, direction: 'desc' };
      }
      // 같은 컬럼: desc → asc → null 순환
      if (prev.direction === 'desc') {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column: null, direction: null };
      }
      return { column, direction: 'desc' };
    });
  }, []);

  const toggleKRowExpand = useCallback((cid) => {
    const key = String(cid);
    setExpandedKRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // 네트워크 그래프 데이터 계산
  const networkData = useMemo(() => {
    if (!kTableRows.length || !mathConceptsMap.size) return { nodes: [], edges: [], nodeMap: new Map(), canvasWidth: 0, canvasHeight: 0, layerCount: 0 };

    // Type 필터 체크 함수
    const passesTypeFilter = (cid) => {
      const concept = mathConceptsMap.get(String(cid));
      if (!concept) return true; // 개념 정보 없으면 통과

      // Math Type 필터
      if (selectedMathTypes.length > 0) {
        if (!concept.math_type || !selectedMathTypes.includes(concept.math_type)) {
          return false;
        }
      }

      // Concept Type 필터
      if (selectedTypes.length > 0) {
        if (!concept.concept_type || !selectedTypes.includes(concept.concept_type)) {
          return false;
        }
      }

      return true;
    };

    // 1. 엣지 수집 (weightRange + Type 필터 적용)
    const allEdges = [];
    const connectedNodeIds = new Set();

    kTableRows.forEach(row => {
      // source 노드 타입 체크
      if (!passesTypeFilter(row.cid)) return;

      row.topEdges.forEach(edge => {
        // target 노드 타입 체크
        if (!passesTypeFilter(edge.dst)) return;

        if (edge.w >= weightRange[0] && edge.w <= weightRange[1]) {
          allEdges.push({
            source: row.cid,
            target: edge.dst,
            weight: edge.w
          });
          connectedNodeIds.add(row.cid);
          connectedNodeIds.add(edge.dst);
        }
      });
    });

    if (connectedNodeIds.size === 0) return { nodes: [], edges: [], nodeMap: new Map(), canvasWidth: 0, canvasHeight: 0, layerCount: 0 };

    // 2. 연결된 노드만 추출
    const nodeMap = new Map();
    connectedNodeIds.forEach(cid => {
      const concept = mathConceptsMap.get(String(cid));
      const rawName = concept?.text || `개념 ${cid}`;
      const displayName = rawName.includes(':') ? rawName.split(':').slice(1).join(':').trim() : rawName;
      const plainName = displayName.replace(/\$([^$]+)\$/g, '$1');

      nodeMap.set(cid, {
        id: cid,
        order: concept?.order || cid,
        name: plainName,
        rawName: displayName,   // $…$ 그대로 — 표 칸은 이걸 renderLatex 로, 캔버스·title 은 plainName
        inDegree: 0,
        outDegree: 0,
        layer: 0,
        subLayer: 0
      });
    });

    // 3. 진입/진출 차수 계산
    allEdges.forEach(edge => {
      const srcNode = nodeMap.get(edge.source);
      const tgtNode = nodeMap.get(edge.target);
      if (srcNode) srcNode.outDegree++;
      if (tgtNode) tgtNode.inDegree++;
    });

    // 4. order 기반 레이어 분할 (균등 분배)
    const nodes = Array.from(nodeMap.values());
    nodes.sort((a, b) => a.order - b.order);

    const MAX_NODES_PER_LAYER = 25;
    const totalLayers = Math.ceil(nodes.length / MAX_NODES_PER_LAYER);

    nodes.forEach((node, idx) => {
      node.layer = Math.floor(idx / MAX_NODES_PER_LAYER);
    });

    // 5. 레이어별 그룹화
    const layerGroups = Array.from({ length: totalLayers }, () => []);
    nodes.forEach(node => {
      layerGroups[node.layer].push(node);
    });

    // 6. 좌표 계산 (레이어별 엇갈린 Y 배치 - staggered layout)
    const NODE_WIDTH = 90;
    const NODE_HEIGHT = 18;
    const LAYER_GAP = 160;
    const NODE_GAP = 24 + NODE_HEIGHT;  // 기존 24 + 노드 높이 18 = 42px
    const PADDING = 50;

    let maxY = 0;
    layerGroups.forEach((group, layerIdx) => {
      // 홀수 레이어는 Y를 NODE_GAP/2 만큼 오프셋 (벽돌 쌓기 패턴)
      const yOffset = (layerIdx % 2 === 1) ? NODE_GAP / 2 : 0;

      group.forEach((node, idx) => {
        node.x = PADDING + layerIdx * LAYER_GAP;
        node.y = PADDING + idx * NODE_GAP + yOffset;
        node.width = NODE_WIDTH;
        node.height = NODE_HEIGHT;
        maxY = Math.max(maxY, node.y);
      });
    });

    // 7. 엣지 경로 계산 (직각)
    const edgesWithPaths = allEdges.map((edge, idx) => {
      const srcNode = nodeMap.get(edge.source);
      const tgtNode = nodeMap.get(edge.target);
      if (!srcNode || !tgtNode) return null;

      const srcX = srcNode.x + NODE_WIDTH;
      const srcY = srcNode.y + NODE_HEIGHT / 2;
      const tgtX = tgtNode.x;
      const tgtY = tgtNode.y + NODE_HEIGHT / 2;

      // 기본 오프셋
      const offset = (idx % 15) * 2 - 14;
      const midX = srcX + (tgtX - srcX) / 2 + offset;

      return {
        ...edge,
        srcX, srcY, tgtX, tgtY, midX,
        midY: (srcY + tgtY) / 2,
        edgeIndex: idx
      };
    }).filter(Boolean);

    const canvasWidth = PADDING * 2 + totalLayers * LAYER_GAP + NODE_WIDTH;
    const canvasHeight = maxY + NODE_HEIGHT + PADDING * 2;

    return {
      nodes,
      edges: edgesWithPaths,
      nodeMap,
      canvasWidth,
      canvasHeight,
      layerCount: totalLayers
    };
  }, [kTableRows, mathConceptsMap, weightRange, selectedTypes, selectedMathTypes]);

  // 선택된 노드의 엣지 통계 계산
  // 용어 정의 (표준):
  //   - "나감" (outgoing): src=선택노드 → dst (선택노드에서 나가는 엣지)
  //   - "들어옴" (incoming): src → dst=선택노드 (선택노드로 들어오는 엣지, 소스별 다중 색상)
  const selectedNodeStats = useMemo(() => {
    if (!selectedNodeId || !networkData.edges.length) return null;

    let outgoingSum = 0;   // src=선택노드 → dst (나감)
    let outgoingCount = 0;
    let incomingSum = 0;   // src → dst=선택노드 (들어옴)
    let incomingCount = 0;

    // "나감" 엣지 타겟 목록 (src=선택노드 → dst)
    const outgoingTargets = [];
    // "들어옴" 엣지를 소스별로 그룹화 (src → dst=선택노드)
    const incomingBySourceMap = new Map();

    networkData.edges.forEach(edge => {
      if (edge.source === selectedNodeId) {
        // src=선택노드 → dst: "나감" (선택노드에서 나가는 엣지)
        outgoingSum += edge.weight;
        outgoingCount++;
        outgoingTargets.push({ target: edge.target, weight: edge.weight });
      } else if (edge.target === selectedNodeId) {
        // src → dst=선택노드: "들어옴" (선택노드로 들어오는 엣지)
        incomingSum += edge.weight;
        incomingCount++;

        // 소스별 그룹화
        const existing = incomingBySourceMap.get(edge.source);
        if (existing) {
          existing.weight += edge.weight;
          existing.count++;
          existing.edges.push(edge);
        } else {
          incomingBySourceMap.set(edge.source, {
            sourceId: edge.source,
            weight: edge.weight,
            count: 1,
            edges: [edge]
          });
        }
      }
    });

    // 소스별 그룹 배열로 변환 및 색상 인덱스 할당 (들어옴)
    const incomingBySource = Array.from(incomingBySourceMap.values())
      .sort((a, b) => b.weight - a.weight) // weight 높은 순 정렬
      .map((group, idx) => ({
        ...group,
        colorIndex: idx % INCOMING_COLORS.length,
        color: INCOMING_COLORS[idx % INCOMING_COLORS.length]
      }));

    // 소스 ID → 색상 인덱스 매핑 (Canvas 렌더링용)
    const sourceColorMap = new Map();
    incomingBySource.forEach(group => {
      sourceColorMap.set(group.sourceId, group.colorIndex);
    });

    // 테이블 데이터에서 해당 노드의 topEdges 합계
    const tableRow = kTableRows.find(r => r.cid === selectedNodeId);
    const tableTopEdgesSum = tableRow?.topEdges?.reduce((sum, e) => sum + (e.w || 0), 0) || 0;
    const tableAllEdgesSum = tableRow?.edges?.reduce((sum, e) => sum + (e.w || 0), 0) || 0;

    return {
      // 나감: src=선택노드 → dst (녹색 단일)
      outgoingSum: outgoingSum.toFixed(2),
      outgoingCount,
      outgoingTargets,
      // 들어옴: src → dst=선택노드 (소스별 다중 색상)
      incomingSum: incomingSum.toFixed(2),
      incomingCount,
      incomingBySource,
      sourceColorMap,
      // 테이블 데이터 (검증용)
      tableTopEdgesSum: tableTopEdgesSum.toFixed(2),
      tableTopEdgesCount: tableRow?.topEdges?.length || 0,
      tableAllEdgesSum: tableAllEdgesSum.toFixed(4),
      tableOutDegree: tableRow?.outDegree || 0
    };
  }, [selectedNodeId, networkData.edges, kTableRows]);

  // 노드 선택이 바뀌면 팝업 닫기
  useEffect(() => {
    setEdgePopup(null);
  }, [selectedNodeId]);

  // 줌 버튼 핸들러
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(z * 1.3, 8));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(z => Math.max(z * 0.7, 0.2));
  }, []);

  // 드래그 임계값 (픽셀) - 이 이상 움직여야 드래그로 간주
  const DRAG_THRESHOLD = 5;
  const mouseDownPosRef = useRef({ x: 0, y: 0 });

  // 팬 핸들러 (마우스) - requestAnimationFrame으로 최적화
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      setIsDragging(true);
      setHasDragged(false);  // 드래그 시작 시 리셋
      mouseDownPosRef.current = { x: e.clientX, y: e.clientY };  // 클릭 시작 위치 저장
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    // 이동 거리 계산 - 임계값 이상이면 드래그로 간주
    const dx = e.clientX - mouseDownPosRef.current.x;
    const dy = e.clientY - mouseDownPosRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance >= DRAG_THRESHOLD) {
      setHasDragged(true);
    }

    // 이전 RAF 취소
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // 터치 핸들러 (모바일)
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const touchStartPosRef = useRef({ x: 0, y: 0 });

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setHasDragged(false);  // 터치 시작 시 리셋
      touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };  // 터치 시작 위치 저장
      setDragStart({
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y
      });
    } else if (e.touches.length === 2) {
      setLastTouchDistance(getTouchDistance(e.touches));
    }
  }, [pan]);

  const handleTouchMove = useCallback((e) => {
    // 이동 거리 계산 - 임계값 이상이면 드래그로 간주
    if (e.touches.length === 1) {
      const dx = e.touches[0].clientX - touchStartPosRef.current.x;
      const dy = e.touches[0].clientY - touchStartPosRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance >= DRAG_THRESHOLD) {
        setHasDragged(true);
      }
    }

    // RAF로 최적화
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      if (e.touches.length === 1 && isDragging) {
        setPan({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y
        });
      } else if (e.touches.length === 2 && lastTouchDistance) {
        const newDistance = getTouchDistance(e.touches);
        if (newDistance) {
          const scale = newDistance / lastTouchDistance;
          setZoom(z => Math.min(Math.max(z * scale, 0.2), 8));
          setLastTouchDistance(newDistance);
        }
      }
    });
  }, [isDragging, dragStart, lastTouchDistance]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setLastTouchDistance(null);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // 줌 리셋
  const handleResetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // 화면에 맞추기
  const handleFitToScreen = useCallback(() => {
    if (!containerRef.current || !networkData.canvasWidth) return;
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const scaleX = containerWidth / networkData.canvasWidth;
    const scaleY = containerHeight / networkData.canvasHeight;
    const newZoom = Math.min(scaleX, scaleY) * 0.9;

    setZoom(Math.min(Math.max(newZoom, 0.2), 8));
    setPan({ x: 0, y: 0 });
  }, [networkData.canvasWidth, networkData.canvasHeight]);

  // Canvas 렌더링
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !networkData.nodes.length) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Canvas 크기 설정
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    ctx.scale(dpr, dpr);

    // 초기화
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.save();

    // 변환 적용
    ctx.translate(displayWidth / 2 + pan.x, displayHeight / 2 + pan.y);
    ctx.scale(zoom, zoom);
    ctx.translate(-networkData.canvasWidth / 2, -networkData.canvasHeight / 2);

    // 가상화: 보이는 영역 계산
    const viewLeft = -pan.x / zoom + networkData.canvasWidth / 2 - displayWidth / 2 / zoom;
    const viewTop = -pan.y / zoom + networkData.canvasHeight / 2 - displayHeight / 2 / zoom;
    const viewRight = viewLeft + displayWidth / zoom;
    const viewBottom = viewTop + displayHeight / zoom;

    // 색상 정의
    const PRIMARY_GREEN = '#10b981';       // 메인 녹색 (나감/outgoing)
    const PRIMARY_GREEN_DARK = '#059669';  // 진한 녹색
    const PRIMARY_GREEN_LIGHT = '#d1fae5'; // 연한 녹색 배경
    const GRAY_LIGHT = '#d1d5db';          // 회색 (비선택 노드) - 엣지라인(#cbd5e1)보다 진하게
    const GRAY_BORDER = '#d1d5db';         // 회색 테두리

    // 선택된 엣지 분리 (outgoing/incoming/normal 3가지)
    // 용어 정의 (표준):
    //   - "나감" (outgoing): src=선택노드 → dst (선택노드에서 나가는 엣지, 녹색 단일)
    //   - "들어옴" (incoming): src → dst=선택노드 (선택노드로 들어오는 엣지, 소스별 다중 색상)
    const outgoingEdges = [];  // 선택 노드 → 타겟 (나감)
    const incomingEdges = [];  // 소스 → 선택 노드 (들어옴)
    const normalEdges = [];    // 비관련
    networkData.edges.forEach(edge => {
      if (selectedNodeId) {
        if (edge.source === selectedNodeId) {
          outgoingEdges.push(edge);  // 나감: 선택 노드 → 타겟
        } else if (edge.target === selectedNodeId) {
          incomingEdges.push(edge);  // 들어옴: 소스 → 선택 노드
        } else {
          normalEdges.push(edge);
        }
      } else {
        normalEdges.push(edge);
      }
    });

    // 엣지 그리기 헬퍼 함수 (직각 경로)
    // edgeType: 'outgoing' | 'incoming' | 'normal'
    //   - 'outgoing' (나감): src=선택노드 → dst, 녹색 단일
    //   - 'incoming' (들어옴): src → dst=선택노드, 소스별 다중 색상 (customColor)
    // customColor: { main, light } - incoming 엣지의 소스별 색상 (optional)
    const drawEdge = (edge, edgeType, spreadIndex = 0, totalSpread = 1, customColor = null) => {
      const minX = Math.min(edge.srcX, edge.tgtX, edge.midX);
      const maxX = Math.max(edge.srcX, edge.tgtX, edge.midX);
      const minY = Math.min(edge.srcY, edge.tgtY);
      const maxY = Math.max(edge.srcY, edge.tgtY);
      if (maxX < viewLeft || minX > viewRight || maxY < viewTop || minY > viewBottom) return;

      const isSelected = edgeType === 'outgoing' || edgeType === 'incoming';
      const isOutgoing = edgeType === 'outgoing';  // 나감: 녹색
      const isIncoming = edgeType === 'incoming';  // 들어옴: 소스별 색상
      const lineWidth = 1 + edge.weight * 3;

      // 색상 결정
      //   - outgoing (나감): 녹색 단일
      //   - incoming (들어옴): customColor 사용 (소스별 다중 색상)
      let edgeColor, edgeColorLight, arrowColor;
      if (isOutgoing) {
        edgeColor = PRIMARY_GREEN;
        edgeColorLight = PRIMARY_GREEN_LIGHT;
        arrowColor = PRIMARY_GREEN;
      } else if (isIncoming && customColor) {
        edgeColor = customColor.main;
        edgeColorLight = customColor.light;
        arrowColor = customColor.main;
      } else {
        // normal 또는 customColor 없는 incoming
        edgeColor = '#94a3b8';
        edgeColorLight = '#e2e8f0';
        arrowColor = '#94a3b8';
      }

      // 선택된 엣지는 노드 영역 밖(상단/하단)으로 우회
      let adjustedMidX = edge.midX;
      let bypassY = null;  // 우회 Y 좌표 (노드 위/아래)
      let srcXOffset = 10;  // 소스 측 수직선 X 오프셋
      let tgtXOffset = 10;  // 타겟 측 수직선 X 오프셋

      if (isSelected) {
        // 선택된 엣지: 노드 영역 밖으로 우회하는 경로
        // 각 엣지마다 고유한 오프셋으로 라인이 겹치지 않도록 함
        const lineSpacing = 20;  // 라인 간격 (px) - 텍스트 박스 높이(16px) + 여유

        // 수평 구간(bypassY) 분산: 각 엣지마다 고유한 Y
        const goUp = spreadIndex % 2 === 0;  // 짝수는 위, 홀수는 아래
        const baseOffset = 25;
        const bypassOffset = baseOffset + spreadIndex * lineSpacing;

        // 소스와 타겟 중 더 높은/낮은 Y 기준으로 우회
        const minY = Math.min(edge.srcY, edge.tgtY);
        const maxY = Math.max(edge.srcY, edge.tgtY);

        if (goUp) {
          bypassY = minY - bypassOffset;  // 위로 우회
        } else {
          bypassY = maxY + bypassOffset;  // 아래로 우회
        }

        // 수직 구간(srcXOffset, tgtXOffset) 분산: 각 엣지마다 고유한 X
        srcXOffset = 8 + spreadIndex * lineSpacing;
        tgtXOffset = 8 + spreadIndex * lineSpacing;

        // midX는 소스와 타겟 중간
        adjustedMidX = (edge.srcX + edge.tgtX) / 2;
      } else if (totalSpread > 1) {
        // 비선택 엣지: 기존 로직
        const srcNode = networkData.nodeMap.get(edge.source);
        const tgtNode = networkData.nodeMap.get(edge.target);
        const layerSpan = srcNode && tgtNode ? Math.abs(tgtNode.layer - srcNode.layer) : 1;

        const baseSpread = 60;
        const layerMultiplier = Math.max(1, layerSpan * 0.8);
        const indexOffset = (spreadIndex - (totalSpread - 1) / 2);
        const spreadStep = baseSpread * layerMultiplier;

        adjustedMidX = edge.midX + indexOffset * spreadStep;
      }

      // 직각 경로 그리기 함수
      const drawPath = () => {
        ctx.beginPath();
        ctx.moveTo(edge.srcX, edge.srcY);

        if (bypassY !== null) {
          // 우회 경로: src → 수직으로 bypass → 수평으로 tgtX 근처 → 수직으로 tgt
          // 각 엣지마다 고유한 오프셋으로 라인이 평행하게 겹치지 않음
          ctx.lineTo(edge.srcX + srcXOffset, edge.srcY);  // 노드에서 떨어짐 (고유 오프셋)
          ctx.lineTo(edge.srcX + srcXOffset, bypassY);    // 우회 높이로 (고유 Y)
          ctx.lineTo(edge.tgtX - tgtXOffset, bypassY);    // 수평 이동 (고유 Y)
          ctx.lineTo(edge.tgtX - tgtXOffset, edge.tgtY);  // 타겟 높이로 (고유 오프셋)
          ctx.lineTo(edge.tgtX, edge.tgtY);               // 타겟으로
        } else {
          // 일반 경로
          ctx.lineTo(adjustedMidX, edge.srcY);
          ctx.lineTo(adjustedMidX, edge.tgtY);
          ctx.lineTo(edge.tgtX, edge.tgtY);
        }
      };

      // 메인 엣지 라인 (깔끔하게)
      drawPath();
      ctx.strokeStyle = isSelected ? edgeColor : '#cbd5e1';
      ctx.lineWidth = isSelected ? lineWidth + 1 : lineWidth;
      ctx.globalAlpha = isSelected ? 1 : 0.4;
      ctx.stroke();

      // 화살표 (외곽선 없이 채우기만)
      const arrowSize = isSelected ? 10 : 7;
      const arrowHalfWidth = arrowSize * 0.6;

      ctx.beginPath();
      ctx.moveTo(edge.tgtX + 1, edge.tgtY);
      ctx.lineTo(edge.tgtX - arrowSize, edge.tgtY - arrowHalfWidth);
      ctx.lineTo(edge.tgtX - arrowSize, edge.tgtY + arrowHalfWidth);
      ctx.closePath();
      ctx.fillStyle = isSelected ? arrowColor : '#94a3b8';
      ctx.globalAlpha = isSelected ? 1 : 0.5;
      ctx.fill();
      ctx.globalAlpha = 1;

      // 텍스트 위치 정보 반환 (수평 우회 라인 위에 배치하기 위해)
      // bypassY가 있으면 그 위치, 없으면 midY 사용
      const textX = (edge.srcX + srcXOffset + edge.tgtX - tgtXOffset) / 2;
      const textY = bypassY !== null ? bypassY : (edge.srcY + edge.tgtY) / 2;

      return { textX, textY, bypassY };
    };

    // 연결된 노드 ID 집합 (빠른 조회용)
    const connectedNodeIds = new Set();
    if (selectedNodeId) {
      connectedNodeIds.add(selectedNodeId);
      networkData.edges.forEach(e => {
        if (e.source === selectedNodeId) connectedNodeIds.add(e.target);
        if (e.target === selectedNodeId) connectedNodeIds.add(e.source);
      });
    }

    // Dim 효과: 선택된 노드가 있을 때 비관련 요소들을 흐리게
    const dimOpacity = selectedNodeId ? 0.15 : 1;

    // 노드 그리기 헬퍼 함수
    const drawNode = (node) => {
      if (node.x + node.width < viewLeft || node.x > viewRight ||
          node.y + node.height < viewTop || node.y > viewBottom) return;

      const isSelected = node.id === selectedNodeId;
      const isConnected = connectedNodeIds.has(node.id);

      // 노드 배경
      ctx.fillStyle = isSelected ? PRIMARY_GREEN : (isConnected ? PRIMARY_GREEN_LIGHT : GRAY_LIGHT);
      ctx.strokeStyle = isSelected ? PRIMARY_GREEN_DARK : (isConnected ? PRIMARY_GREEN : GRAY_BORDER);
      ctx.lineWidth = isSelected ? 2 : 1;

      ctx.beginPath();
      ctx.roundRect(node.x, node.y, node.width, node.height, 4);
      ctx.fill();
      ctx.stroke();

      // order 배경
      ctx.fillStyle = isSelected ? PRIMARY_GREEN_DARK : (isConnected ? PRIMARY_GREEN : '#9ca3af');
      ctx.beginPath();
      ctx.roundRect(node.x, node.y, 24, node.height, [4, 0, 0, 4]);
      ctx.fill();

      // order 텍스트
      ctx.font = 'bold 9px system-ui';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText(String(node.order), node.x + 12, node.y + 13);

      // 개념명
      ctx.font = '8px system-ui';
      ctx.fillStyle = isSelected ? '#ffffff' : '#374151';
      ctx.textAlign = 'left';
      const displayName = node.name.length > 10 ? node.name.substring(0, 10) + '..' : node.name;
      ctx.fillText(displayName, node.x + 28, node.y + 13);
    };

    // 선택된 노드가 없을 때: 엣지 → 노드 순서로 그려서 노드가 앞에 보이도록
    // 선택된 노드가 있을 때: 비연결 노드 → 엣지 → 연결 노드 → 선택 노드 순서

    if (!selectedNodeId) {
      // 선택 없음: 모든 엣지 먼저 그리기
      normalEdges.forEach(edge => drawEdge(edge, 'normal'));

      // 그 다음 모든 노드 그리기 (노드가 엣지 위에 표시)
      networkData.nodes.forEach(node => drawNode(node));
    } else {
      // 선택 있음: 비연결 노드 먼저 그리기 (dim 처리)
      ctx.globalAlpha = dimOpacity;
      networkData.nodes.forEach(node => {
        const isConnected = connectedNodeIds.has(node.id);
        if (!isConnected) {
          drawNode(node);
        }
      });
      ctx.globalAlpha = 1;

      // 비선택 엣지 그리기 (dim 처리)
      ctx.globalAlpha = dimOpacity;
      normalEdges.forEach(edge => drawEdge(edge, 'normal'));
      ctx.globalAlpha = 1;
    }

    // 3. 선택된 엣지 그리기 (incoming/outgoing 분리)
    // 선택 노드의 엣지 시작/끝 Y 위치 분산을 위한 계산
    const NODE_HEIGHT = 18;
    const selectedNode = selectedNodeId ? networkData.nodeMap.get(selectedNodeId) : null;
    const totalOutgoing = outgoingEdges.length;
    const totalIncoming = incomingEdges.length;
    const totalSelectedEdges = totalOutgoing + totalIncoming;  // 전체 선택 엣지 수 (연속 인덱스용)

    // 선택 노드에서 시작하는 엣지들의 srcY 분산 (나감: src=선택노드)
    const getOutgoingSrcY = (idx, total) => {
      if (!selectedNode || total <= 1) return null;
      const spreadRange = Math.min(NODE_HEIGHT * 0.8, total * 3);
      const step = spreadRange / Math.max(1, total - 1);
      const baseY = selectedNode.y + NODE_HEIGHT / 2;
      return baseY - spreadRange / 2 + idx * step;
    };

    // 선택 노드로 끝나는 엣지들의 tgtY 분산 (들어옴: dst=선택노드)
    const getIncomingTgtY = (idx, total) => {
      if (!selectedNode || total <= 1) return null;
      const spreadRange = Math.min(NODE_HEIGHT * 0.8, total * 3);
      const step = spreadRange / Math.max(1, total - 1);
      const baseY = selectedNode.y + NODE_HEIGHT / 2;
      return baseY - spreadRange / 2 + idx * step;
    };

    // Outgoing 엣지 (나감: 선택노드 → 타겟, 녹색 단일)
    // spreadIndex: 0, 1, 2, ... (totalOutgoing - 1)
    const outgoingEdgePositions = outgoingEdges.map((edge, idx) => {
      // 선택 노드의 srcY 위치 분산 적용
      const adjustedSrcY = getOutgoingSrcY(idx, totalOutgoing);
      const adjustedEdge = adjustedSrcY !== null ? { ...edge, srcY: adjustedSrcY } : edge;

      // spreadIndex는 idx, totalSpread는 전체 선택 엣지 수
      const posInfo = drawEdge(adjustedEdge, 'outgoing', idx, totalSelectedEdges);

      return {
        edge: adjustedEdge,
        edgeType: 'outgoing',
        textX: posInfo?.textX,
        textY: posInfo?.textY
      };
    });

    // Incoming 엣지 (들어옴: 소스 → 선택노드, 소스별 다중 색상)
    // spreadIndex: totalOutgoing, totalOutgoing + 1, ... (연속 인덱스)
    const incomingEdgePositions = incomingEdges.map((edge, idx) => {
      // selectedNodeStats에서 소스별 색상 매핑 가져오기
      const colorIndex = selectedNodeStats?.sourceColorMap?.get(edge.source);
      const customColor = colorIndex !== undefined ? INCOMING_COLORS[colorIndex] : null;

      // 선택 노드의 tgtY 위치 분산 적용
      const adjustedTgtY = getIncomingTgtY(idx, totalIncoming);
      const adjustedEdge = adjustedTgtY !== null ? { ...edge, tgtY: adjustedTgtY } : edge;

      // spreadIndex는 totalOutgoing + idx로 연속화
      const posInfo = drawEdge(adjustedEdge, 'incoming', totalOutgoing + idx, totalSelectedEdges, customColor);

      return {
        edge: adjustedEdge,
        edgeType: 'incoming',
        textX: posInfo?.textX,
        textY: posInfo?.textY,
        customColor
      };
    });

    // 모든 선택된 엣지 위치 합치기
    const selectedEdgePositions = [...incomingEdgePositions, ...outgoingEdgePositions];

    // 4. 연결된 노드 그리기 (선택 엣지 위에 표시) - 선택된 노드가 있을 때만
    if (selectedNodeId) {
      networkData.nodes.forEach(node => {
        const isSelected = node.id === selectedNodeId;
        const isConnected = connectedNodeIds.has(node.id);
        // 연결된 노드만 (선택된 노드 제외 - 마지막에 그림)
        if (isConnected && !isSelected) {
          drawNode(node);
        }
      });

      // 5. 선택된 노드 그리기 (맨 앞에 표시)
      const selectedNode = networkData.nodeMap.get(selectedNodeId);
      if (selectedNode) {
        drawNode(selectedNode);
      }
    }

    // 6. 가중치 텍스트 (맨 앞에 표시 - 텍스트 겹침 방지)
    // 텍스트 위치 충돌 감지용 배열
    const placedTextBoxes = [];

    // 텍스트 박스 충돌 검사 함수
    const checkCollision = (box1, box2) => {
      return !(box1.right < box2.left || box1.left > box2.right ||
               box1.bottom < box2.top || box1.top > box2.bottom);
    };

    // 겹치지 않는 위치 찾기
    const findNonOverlappingY = (baseX, baseY, boxW, boxH) => {
      const margin = 2;  // 박스 간 최소 간격
      let testY = baseY;
      let attempts = 0;
      const maxAttempts = 20;
      const shiftAmount = boxH + margin;

      while (attempts < maxAttempts) {
        const testBox = {
          left: baseX - boxW / 2 - margin,
          right: baseX + boxW / 2 + margin,
          top: testY - boxH / 2 - margin,
          bottom: testY + boxH / 2 + margin
        };

        let hasCollision = false;
        for (const placed of placedTextBoxes) {
          if (checkCollision(testBox, placed)) {
            hasCollision = true;
            break;
          }
        }

        if (!hasCollision) {
          return testY;
        }

        // 위아래로 번갈아 시도
        if (attempts % 2 === 0) {
          testY = baseY - shiftAmount * Math.ceil((attempts + 1) / 2);
        } else {
          testY = baseY + shiftAmount * Math.ceil((attempts + 1) / 2);
        }
        attempts++;
      }

      return baseY;  // 최대 시도 초과시 원래 위치
    };

    selectedEdgePositions.forEach(({ edge, edgeType, textX: baseTextX, textY: baseTextY, customColor }, idx) => {
      if (baseTextX !== undefined && baseTextY !== undefined) {
        // 방향 화살표와 색상 결정
        // 나감(outgoing): "엣지값 ➡" (선택노드에서 나감)
        // 들어옴(incoming): "➡ 엣지값" (선택노드로 들어옴)
        const arrowSymbol = '➡';
        const isOutgoing = edgeType === 'outgoing';

        // 색상 규칙:
        //   - outgoing (나감): 라인과 텍스트 같은 녹색
        //   - incoming (들어옴): 라인과 텍스트 색상 다르게 (박스 테두리=라인색, 텍스트=검정)
        const borderColor = customColor ? customColor.main : PRIMARY_GREEN;
        const textColor = isOutgoing ? PRIMARY_GREEN : '#1f2937';  // outgoing=녹색, incoming=검정

        const weightText = edge.weight.toFixed(2);

        // 전체 텍스트 너비 계산
        ctx.font = 'bold 11px system-ui';
        const arrowTextWidth = ctx.measureText(arrowSymbol).width;
        ctx.font = 'bold 10px system-ui';
        const numWidth = ctx.measureText(weightText).width;
        const totalWidth = arrowTextWidth + 4 + numWidth;

        const boxW = totalWidth + 12;
        const boxH = 16;

        // 겹치지 않는 Y 위치 찾기 (라인 위에 배치)
        const finalTextY = findNonOverlappingY(baseTextX, baseTextY, boxW, boxH);
        const finalTextX = baseTextX;

        // 배치된 박스 기록
        placedTextBoxes.push({
          left: finalTextX - boxW / 2,
          right: finalTextX + boxW / 2,
          top: finalTextY - boxH / 2,
          bottom: finalTextY + boxH / 2
        });

        const boxX = finalTextX - totalWidth / 2 - 6;
        const boxY = finalTextY - 8;
        const radius = 8;

        // 배경 박스 (테두리는 라인 색상)
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxW, boxH, radius);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fill();
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.textAlign = 'left';
        const startX = finalTextX - totalWidth / 2;

        if (isOutgoing) {
          // 나감: "엣지값 ➡"
          ctx.font = 'bold 10px system-ui';
          ctx.fillStyle = textColor;
          ctx.fillText(weightText, startX, finalTextY + 4);

          ctx.font = 'bold 12px system-ui';
          ctx.fillStyle = borderColor;
          ctx.fillText('➡', startX + numWidth + 4, finalTextY + 4);
        } else {
          // 들어옴: "➡ 엣지값"
          ctx.font = 'bold 12px system-ui';
          ctx.fillStyle = borderColor;
          ctx.fillText('➡', startX, finalTextY + 4);

          ctx.font = 'bold 10px system-ui';
          ctx.fillStyle = textColor;
          ctx.fillText(weightText, startX + arrowTextWidth + 4, finalTextY + 4);
        }
      }
    });

    ctx.restore();
  }, [networkData, zoom, pan, selectedNodeId, selectedNodeStats]);

  // Canvas 렌더링 트리거
  useEffect(() => {
    if (activeTab === 'graph') {
      drawCanvas();
    }
  }, [activeTab, drawCanvas]);

  // Canvas wheel 이벤트 리스너 (passive: false 필요)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTab !== 'graph') return;

    const wheelHandler = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom(z => Math.min(Math.max(z * delta, 0.2), 8));
    };

    canvas.addEventListener('wheel', wheelHandler, { passive: false });
    return () => canvas.removeEventListener('wheel', wheelHandler);
  }, [activeTab]);

  // 노드 클릭 핸들러
  const handleCanvasClick = useCallback((e) => {
    // 드래그 후에는 클릭 무시 (드래그로 노드 선택 해제 방지)
    if (hasDragged) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;

    // 마우스 좌표를 캔버스 좌표로 변환
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // 변환 역산
    const canvasX = (mouseX - displayWidth / 2 - pan.x) / zoom + networkData.canvasWidth / 2;
    const canvasY = (mouseY - displayHeight / 2 - pan.y) / zoom + networkData.canvasHeight / 2;

    // 클릭된 노드 찾기
    const clickedNode = networkData.nodes.find(node =>
      canvasX >= node.x && canvasX <= node.x + node.width &&
      canvasY >= node.y && canvasY <= node.y + node.height
    );

    setSelectedNodeId(clickedNode ? clickedNode.id : null);
  }, [networkData, zoom, pan, hasDragged]);

  return (
    <div className="flex min-h-0 flex-1 flex-col" style={{ minHeight: '400px' }}>
      {/* 헤더 */}
      <div className="mb-2 flex items-center justify-between rounded-lg bg-gray-50 p-2">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-gray-900">K 행렬 보기</h3>
        </div>
        <button
          onClick={handleForceReload}
          disabled={isLoadingK}
          className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoadingK ? 'animate-spin' : ''}`} />
          <span>{isLoadingK ? '로딩 중...' : '강제 리로드'}</span>
        </button>
      </div>

      {/* 에러 표시 */}
      {kError && (
        <div className="mb-4 flex shrink-0 items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <p>{kError}</p>
        </div>
      )}

      {/* 탭 헤더 */}
      <div className="mb-2 flex items-center border-b border-gray-200">
        <button
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${
            activeTab === 'graph'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('graph')}
        >
          <BarChart3 className="h-4 w-4" />
          <span>그래프</span>
        </button>
        <button
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${
            activeTab === 'table'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('table')}
        >
          <Table className="h-4 w-4" />
          <span>원본 테이블</span>
        </button>
      </div>

      {/* 그래프 탭 컨텐츠 */}
      {activeTab === 'graph' && (
        <div className="min-h-0 flex-1 flex flex-col rounded-lg border border-gray-200 overflow-visible">
          {isLoadingK ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              K 로딩 중...
            </div>
          ) : (kMeta && kRows && kVectorNi && kVectorSRow) ? (
            <>
              {/* 그래프 컨트롤 바 - 1행: Type 필터 + 기본 통계 + 슬라이더 */}
              <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 border-b bg-gray-50 px-2 py-1 text-[10px] text-gray-600">
                {/* Type 필터 */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTypeFilterPosition({ top: rect.bottom + 4, left: rect.left });
                      setShowTypeFilter(!showTypeFilter);
                    }}
                    className={`flex items-center gap-1 rounded-3xl px-2 py-1 text-xs transition-colors ${
                      totalSelectedFilters > 0
                        ? 'bg-primary/20 text-primary'
                        : 'bg-white text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>Type</span>
                    {totalSelectedFilters > 0 && (
                      <span className="rounded-full bg-primary px-1.5 text-white">
                        {totalSelectedFilters}
                      </span>
                    )}
                    <ChevronDown className={`h-3 w-3 transition-transform ${showTypeFilter ? 'rotate-180' : ''}`} />
                  </button>

                  {showTypeFilter && createPortal(
                    <>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 9999990 }}
                        onClick={() => setShowTypeFilter(false)}
                      />
                      <div
                        className="fixed rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
                        style={{
                          top: `${typeFilterPosition.top}px`,
                          left: `${typeFilterPosition.left}px`,
                          width: '320px',
                          zIndex: 9999991,
                        }}
                      >
                        <div className="flex">
                          <div className="w-1/2 border-r border-gray-100 px-2">
                            <div className="mb-1 flex items-center justify-between px-1">
                              <span className="text-[10px] font-semibold text-gray-500">Math Type</span>
                              <button
                                onClick={toggleAllMathTypes}
                                className="text-[9px] text-primary hover:underline"
                              >
                                {selectedMathTypes.length === MATH_TYPE_LIST.length ? '해제' : '전체'}
                              </button>
                            </div>
                            {MATH_TYPE_LIST.map(type => (
                              <button
                                key={type.code}
                                onClick={() => toggleMathTypeFilter(type.code)}
                                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50"
                              >
                                <span
                                  className="inline-flex h-4 w-8 items-center justify-center rounded text-[9px] font-semibold"
                                  style={{ backgroundColor: getMathTypeColor(type.code) }}
                                >
                                  {type.code}
                                </span>
                                <span className="flex-1 text-left">{type.name}</span>
                                {selectedMathTypes.includes(type.code) && (
                                  <Check className="h-3 w-3 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>

                          <div className="w-1/2 px-2">
                            <div className="mb-1 flex items-center justify-between px-1">
                              <span className="text-[10px] font-semibold text-gray-500">Concept Type</span>
                              <button
                                onClick={toggleAllConceptTypes}
                                className="text-[9px] text-primary hover:underline"
                              >
                                {selectedTypes.length === CONCEPT_TYPES.length ? '해제' : '전체'}
                              </button>
                            </div>
                            {CONCEPT_TYPES.map(type => (
                              <button
                                key={type.code}
                                onClick={() => toggleConceptTypeFilter(type.code)}
                                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50"
                              >
                                <span
                                  className="inline-flex h-4 w-8 items-center justify-center rounded text-[9px] font-semibold"
                                  style={{ backgroundColor: getConceptTypeColor(type.code) }}
                                >
                                  {type.code}
                                </span>
                                <span className="flex-1 text-left">{type.name}</span>
                                {selectedTypes.includes(type.code) && (
                                  <Check className="h-3 w-3 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>

                        {totalSelectedFilters > 0 && (
                          <>
                            <div className="mx-2 my-2 border-t border-gray-100" />
                            <button
                              onClick={resetAllFilters}
                              className="w-full px-3 py-1.5 text-xs text-red-500 hover:bg-red-50"
                            >
                              필터 초기화
                            </button>
                          </>
                        )}
                      </div>
                    </>,
                    document.body
                  )}
                </div>
                {/* 좌측: 기본 통계 */}
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="whitespace-nowrap">노드: <span className="font-semibold text-primary">{networkData.nodes.length}</span></span>
                  <span className="whitespace-nowrap">엣지: <span className="font-semibold text-primary">{networkData.edges.length}</span></span>
                  <span className="whitespace-nowrap">레이어: <span className="font-semibold">{networkData.layerCount}</span></span>
                </div>
                {/* 중앙: 범위 슬라이더 */}
                <div className="flex items-center gap-1" title="엣지 가중치 범위 필터">
                  <span className="whitespace-nowrap">가중치:</span>
                  <div className="relative h-6 w-32">
                    {/* 트랙 배경 */}
                    <div className="absolute top-2.5 left-0 right-0 h-1 rounded bg-gray-200" />
                    {/* 선택 범위 표시 */}
                    <div
                      className="absolute top-2.5 h-1 rounded bg-primary"
                      style={{
                        left: `${weightRange[0] * 100}%`,
                        right: `${(1 - weightRange[1]) * 100}%`
                      }}
                    />
                    {/* Min 슬라이더 */}
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={weightRange[0]}
                      onChange={(e) => {
                        const newMin = parseFloat(e.target.value);
                        setWeightRange([Math.min(newMin, weightRange[1] - 0.01), weightRange[1]]);
                      }}
                      className="absolute top-0 left-0 z-20 w-full h-6 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                    />
                    {/* Max 슬라이더 */}
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={weightRange[1]}
                      onChange={(e) => {
                        const newMax = parseFloat(e.target.value);
                        setWeightRange([weightRange[0], Math.max(newMax, weightRange[0] + 0.01)]);
                      }}
                      className="absolute top-0 left-0 z-10 w-full h-6 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                    />
                  </div>
                  <span className="w-[60px] text-xs font-semibold tabular-nums">{weightRange[0].toFixed(2)}~{weightRange[1].toFixed(2)}</span>
                </div>
              </div>

              {/* 그래프 컨트롤 바 - 2행: 선택 노드 정보 (노드 선택 시에만 표시) */}
              {selectedNodeId && (
                <div className="relative flex items-center gap-2 flex-wrap border-b bg-indigo-50 px-2 py-1 text-[10px] text-gray-600">
                  {/* 선택된 노드 */}
                  <span className="truncate rounded bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700">
                    선택: {selectedNodeId}
                  </span>
                  {/* 나감: 선택노드 → 타겟 (녹색 단일) - 클릭으로 팝업 */}
                  <button
                    ref={outgoingBtnRef}
                    onClick={() => {
                      if (edgePopup?.type === 'outgoing') {
                        setEdgePopup(null);
                      } else {
                        const rect = outgoingBtnRef.current?.getBoundingClientRect();
                        setEdgePopup({ type: 'outgoing', position: { x: rect?.left || 0, y: (rect?.bottom || 0) + 4 } });
                      }
                    }}
                    className="flex items-center gap-1 whitespace-nowrap rounded bg-emerald-50 px-2 py-0.5 font-medium text-emerald-600 hover:bg-emerald-100 transition-colors"
                    title="클릭하여 나가는 엣지 상세 보기"
                  >
                    <span>나감</span>
                    <span className="text-emerald-500">➡</span>
                    {selectedNodeStats && (
                      <span className="ml-1 text-emerald-700">
                        ({selectedNodeStats.outgoingCount}개, Σ={selectedNodeStats.outgoingSum})
                      </span>
                    )}
                  </button>
                  {/* 들어옴: 소스 → 선택노드 (소스별 다중 색상) - 클릭으로 팝업 */}
                  <button
                    ref={incomingBtnRef}
                    onClick={() => {
                      if (edgePopup?.type === 'incoming') {
                        setEdgePopup(null);
                      } else {
                        const rect = incomingBtnRef.current?.getBoundingClientRect();
                        setEdgePopup({ type: 'incoming', position: { x: rect?.left || 0, y: (rect?.bottom || 0) + 4 } });
                      }
                    }}
                    className="flex items-center gap-1 flex-wrap hover:bg-indigo-100 rounded px-1 py-0.5 transition-colors"
                    title="클릭하여 들어오는 엣지 상세 보기"
                  >
                    {selectedNodeStats?.incomingBySource?.length > 0 ? (
                      <>
                        <span className="text-gray-500">➡들어옴:</span>
                        {selectedNodeStats.incomingBySource.map((group) => (
                          <span
                            key={group.sourceId}
                            className="flex items-center gap-0.5 whitespace-nowrap rounded px-1.5 py-0.5 text-white"
                            style={{ backgroundColor: group.color.main }}
                          >
                            <span>{group.sourceId}</span>
                            <span className="opacity-80">({group.weight.toFixed(2)})</span>
                          </span>
                        ))}
                        <span className="text-gray-500 ml-1">
                          총 {selectedNodeStats.incomingCount}개, Σ={selectedNodeStats.incomingSum}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>➡</span>
                        <span className="text-gray-500">들어옴 없음</span>
                      </>
                    )}
                  </button>
                  {/* 테이블 데이터 검증용 */}
                  {selectedNodeStats && (
                    <span className="flex items-center gap-1 whitespace-nowrap rounded bg-gray-100 px-2 py-0.5 text-gray-600" title="테이블 topEdges 합계 (검증용)">
                      <span className="text-gray-500">테이블:</span>
                      <span className="text-gray-700">
                        top5 Σ={selectedNodeStats.tableTopEdgesSum}, 전체 Σ={selectedNodeStats.tableAllEdgesSum}
                      </span>
                    </span>
                  )}
                </div>
              )}

              {/* Canvas 네트워크 그래프 */}
              <div
                ref={containerRef}
                className="relative min-h-0 flex-1 overflow-hidden bg-white cursor-grab active:cursor-grabbing touch-none"
              >
                {networkData.nodes.length > 0 ? (
                  <canvas
                    ref={canvasRef}
                    className="h-full w-full"
                    onClick={handleCanvasClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-400 text-sm">
                    연결된 노드가 없습니다.
                  </div>
                )}

                {/* 플로팅 줌 컨트롤 - 하단 중앙 */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-lg border border-gray-200 bg-white/90 px-2 py-1 shadow-md backdrop-blur-sm">
                  <button
                    onClick={handleZoomOut}
                    className="flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200"
                    title="축소"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <span className="min-w-[45px] text-center text-[10px] text-gray-500">
                    {(zoom * 100).toFixed(0)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200"
                    title="확대"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleFitToScreen}
                    className="flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200"
                    title="화면에 맞추기"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleResetView}
                    className="ml-1 rounded border border-gray-300 bg-white px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-100"
                  >
                    리셋
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-10 text-gray-500">
              K 데이터가 없습니다.
            </div>
          )}
        </div>
      )}

      {/* 원본 테이블 탭 컨텐츠 */}
      {activeTab === 'table' && (
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto rounded-lg border border-gray-200">
          {isLoadingK ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              K 로딩 중...
            </div>
          ) : (kMeta && kRows && kVectorNi && kVectorSRow) ? (
            <div className="min-h-0">
              <div className="flex flex-wrap items-center gap-4 border-b bg-gray-50 px-3 py-2 text-xs text-gray-700">
                <span><span className="font-semibold">K</span>: {kMeta.seq} (prev {kMeta.prev_seq ?? '-'})</span>
                <span><span className="font-semibold">w_scale</span>: {kRows.w_scale ?? 10000}</span>
                <span className="text-gray-500">
                  * 정렬: {tableSort.column ? (
                    <>
                      <span className="font-medium text-gray-700">
                        {tableSort.column === 'cid' ? 'Concept' :
                         tableSort.column === 'n_i' ? 'N_i' :
                         tableSort.column === 's_row' ? 's_row' :
                         tableSort.column === 'outDegree' ? 'out_deg' :
                         tableSort.column === 'topEdges' ? 'top edges(개수)' : tableSort.column}
                      </span>
                      {' '}({tableSort.direction === 'desc' ? '내림차순' : '오름차순'})
                    </>
                  ) : '기본 (concept_index)'}
                </span>
              </div>

            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-100">
                  <th
                    className="sticky left-0 z-20 min-w-[240px] border border-gray-200 bg-gray-100 px-2 py-1 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                    onClick={() => handleTableSort('cid')}
                  >
                    <span className="flex items-center gap-1">
                      Concept (concept_index)
                      <span className="text-[10px] text-gray-400">
                        {tableSort.column === 'cid' ? (tableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                      </span>
                    </span>
                  </th>
                  <th
                    className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                    onClick={() => handleTableSort('n_i')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      N_i
                      <span className="text-[10px] text-gray-400">
                        {tableSort.column === 'n_i' ? (tableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                      </span>
                    </span>
                  </th>
                  <th
                    className="min-w-[110px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                    onClick={() => handleTableSort('s_row')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      s_row
                      <span className="text-[10px] text-gray-400">
                        {tableSort.column === 's_row' ? (tableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                      </span>
                    </span>
                  </th>
                  <th
                    className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                    onClick={() => handleTableSort('outDegree')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      out_deg
                      <span className="text-[10px] text-gray-400">
                        {tableSort.column === 'outDegree' ? (tableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                      </span>
                    </span>
                  </th>
                  <th
                    className="min-w-[420px] border border-gray-200 px-2 py-1 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                    onClick={() => handleTableSort('topEdges')}
                  >
                    <span className="flex items-center gap-1">
                      top edges
                      <span className="text-[10px] text-gray-400">
                        {tableSort.column === 'topEdges' ? (tableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                      </span>
                      <span className="text-[9px] text-gray-400 ml-1">(개수)</span>
                    </span>
                  </th>
                  <th className="min-w-[90px] border border-gray-200 px-2 py-1 text-center text-xs font-semibold text-gray-700">상세</th>
                </tr>
              </thead>
              <tbody>
                {sortedTableRows.map((row) => {
                  const expanded = expandedKRows.has(String(row.cid));
                  return (
                    <tr key={row.cid} className="hover:bg-gray-50/50">
                      <td className="sticky left-0 z-10 border border-gray-200 bg-white px-2 py-1 text-sm align-top">
                        <div className="flex items-start gap-2">
                          <span className="inline-flex h-5 min-w-[56px] items-center justify-center rounded bg-gray-100 px-2 text-[10px] font-semibold text-gray-700">
                            {row.cid}
                          </span>
                          <span
                            className="min-w-0 flex-1 font-medium text-gray-800 line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: row.nameRendered }}
                          />
                        </div>
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-xs">{row.n_i}</td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.s_row === 'number' ? row.s_row.toFixed(6) : row.s_row}</td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-xs">{row.outDegree}</td>
                      <td className="border border-gray-200 px-2 py-1 text-xs">
                        {row.topEdges.length === 0 ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {row.topEdges.map((e, idx) => (
                              <span key={`${e.dst}-${idx}`} className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-800">
                                {e.dst}: <span className="font-semibold">{typeof e.w === 'number' ? e.w.toFixed(4) : e.w}</span>
                              </span>
                            ))}
                          </div>
                        )}

                        {expanded && (
                          <div className="mt-2 max-h-[180px] overflow-auto rounded border bg-white p-2">
                            {row.edges.length === 0 ? (
                              <div className="text-gray-400">dst 없음</div>
                            ) : (
                              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                                {row.edges.map((e, idx) => (
                                  <div key={`${e.dst}-${idx}`} className="rounded bg-gray-50 px-2 py-1 text-[11px]">
                                    <div className="font-semibold text-gray-800">{e.dst}</div>
                                    <div className="text-gray-600">{typeof e.w === 'number' ? e.w.toFixed(4) : e.w}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-center text-xs">
                        <button
                          className="rounded-3xl border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                          onClick={() => toggleKRowExpand(row.cid)}
                        >
                          {expanded ? '접기' : '펼치기'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center py-10 text-gray-500">
              K 데이터가 없습니다.
            </div>
          )}
        </div>
      )}

      {/* 엣지 팝업 (Portal로 body에 직접 렌더링) */}
      {edgePopup && selectedNodeStats && createPortal(
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[220px] max-h-[350px] overflow-auto text-[11px]"
          style={{
            left: edgePopup.position?.x || 0,
            top: edgePopup.position?.y || 0,
            zIndex: 99999999
          }}
        >
          {edgePopup.type === 'outgoing' ? (
            <>
              <div className="flex items-center justify-between mb-2 pb-2 border-b">
                <span className="font-semibold text-emerald-700">나감 ({selectedNodeStats.outgoingCount}개)</span>
                <button onClick={() => setEdgePopup(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
              </div>
              {selectedNodeStats.outgoingTargets?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="text-gray-500 border-b">
                      <th className="text-left py-1 pr-2">ID</th>
                      <th className="text-left py-1 pr-2">개념명</th>
                      <th className="text-right py-1">가중치</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedNodeStats.outgoingTargets
                      .sort((a, b) => b.weight - a.weight)
                      .map((item, idx) => {
                        const node = networkData.nodeMap.get(item.target);
                        return (
                          <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-1.5 pr-2 text-gray-500 text-[10px]">{item.target}</td>
                            {/* eslint-disable-next-line react/no-danger -- katex 출력(개념 사전 값) */}
                            <td className="py-1.5 pr-2 text-gray-700 max-w-[150px] truncate" title={node?.name || ''} dangerouslySetInnerHTML={{ __html: node ? renderLatex(node.rawName || node.name) : '-' }} />
                            <td className="py-1.5 text-right font-mono text-emerald-600">{item.weight.toFixed(4)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold text-emerald-700">
                      <td colSpan={2} className="py-2 pt-3">합계</td>
                      <td className="py-2 pt-3 text-right font-mono">{selectedNodeStats.outgoingSum}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-gray-400 py-3 text-center">나가는 엣지 없음</div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 pb-2 border-b">
                <span className="font-semibold text-blue-700">들어옴 ({selectedNodeStats.incomingCount}개)</span>
                <button onClick={() => setEdgePopup(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
              </div>
              {selectedNodeStats.incomingBySource?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="text-gray-500 border-b">
                      <th className="text-left py-1 pr-2">ID</th>
                      <th className="text-left py-1 pr-2">개념명</th>
                      <th className="text-right py-1">가중치</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedNodeStats.incomingBySource.map((group) => {
                      const node = networkData.nodeMap.get(group.sourceId);
                      return (
                        <tr key={group.sourceId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 pr-2">
                            <span
                              className="inline-block px-2 py-0.5 rounded text-white text-[10px]"
                              style={{ backgroundColor: group.color.main }}
                            >
                              {group.sourceId}
                            </span>
                          </td>
                          {/* eslint-disable-next-line react/no-danger -- katex 출력(개념 사전 값) */}
                          <td className="py-1.5 pr-2 text-gray-700 max-w-[150px] truncate" title={node?.name || ''} dangerouslySetInnerHTML={{ __html: node ? renderLatex(node.rawName || node.name) : '-' }} />
                          <td className="py-1.5 text-right font-mono text-blue-600">{group.weight.toFixed(4)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold text-blue-700">
                      <td colSpan={2} className="py-2 pt-3">합계</td>
                      <td className="py-2 pt-3 text-right font-mono">{selectedNodeStats.incomingSum}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-gray-400 py-3 text-center">들어오는 엣지 없음</div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default KMatrixContent;
